(() => {
  if (window.__faDeliveryRulesBootstrapped) return;
  window.__faDeliveryRulesBootstrapped = true;

  const EXPLICIT_CITY_KEY = 'fa_delivery_explicit_city_v2';
  const LEGACY_CITY_KEY = 'selected_city';
  const observedRegions = new WeakSet();

  function initRules(widget) {
    if (!widget || widget.dataset.faDeliveryRulesReady === 'true') return;
    widget.dataset.faDeliveryRulesReady = 'true';

    const form = widget.closest('form[action*="/cart/add"]');
    if (!form) return;

    const citySelect = widget.querySelector('[data-fa-city]');
    const datePicker = widget.querySelector('[data-fa-date-picker]');
    const timeslotSelect = widget.querySelector('[data-fa-timeslot-select]');
    const specificWrap = widget.querySelector('[data-fa-specific-wrap]');
    const specificSelect = widget.querySelector('[data-fa-specific-select]');
    const status = widget.querySelector('[data-fa-status]');
    const modal = form.querySelector('[data-fa-delivery-modal]');
    const modalDialog = modal?.querySelector('[data-fa-delivery-modal-dialog]');
    const modalTitle = modal?.querySelector('[data-fa-delivery-modal-title]');
    const modalMessage = modal?.querySelector('[data-fa-delivery-modal-message]');
    const modalAction = modal?.querySelector('[data-fa-delivery-modal-action]');
    const modalCloseControls = modal?.querySelectorAll('[data-fa-delivery-modal-close]') || [];

    let missingField = citySelect;
    let returnFocus = null;
    let suppressCityMarker = false;

    const setRequiredState = () => {
      [citySelect, datePicker, timeslotSelect].forEach((field) => {
        if (!field) return;
        field.required = true;
        field.setAttribute('aria-required', 'true');
      });

      if (specificSelect) {
        const required = Boolean(specificWrap && !specificWrap.hidden);
        specificSelect.required = required;
        specificSelect.setAttribute('aria-required', required ? 'true' : 'false');
      }
    };

    const setInlineError = (message) => {
      if (!status) return;
      status.textContent = message;
      status.dataset.state = 'error';
    };

    const closeModal = ({ restoreFocus = true } = {}) => {
      if (!modal || modal.hidden) return;
      modal.hidden = true;
      document.body.classList.remove('fa-delivery-modal-open');

      if (restoreFocus && returnFocus instanceof HTMLElement && document.contains(returnFocus)) {
        returnFocus.focus({ preventScroll: true });
      }
    };

    const focusMissingField = () => {
      const target = missingField;
      closeModal({ restoreFocus: false });
      if (!(target instanceof HTMLElement)) return;

      widget.scrollIntoView({ behavior: 'smooth', block: 'center' });
      window.setTimeout(() => {
        target.focus({ preventScroll: true });
        if (target === datePicker) {
          try { target.showPicker?.(); } catch (_) {}
        }
      }, 260);
    };

    const openModal = ({ title = 'Complete delivery details', message, field } = {}) => {
      missingField = field || citySelect;
      setInlineError(message);

      if (!modal || !modalDialog || !modalMessage) {
        focusMissingField();
        return;
      }

      returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      if (modalTitle) modalTitle.textContent = title;
      modalMessage.textContent = message;
      modal.hidden = false;
      document.body.classList.add('fa-delivery-modal-open');

      requestAnimationFrame(() => modalDialog.focus({ preventScroll: true }));
    };

    const deliveryValidation = () => {
      if (!citySelect?.value) {
        const unavailable = Boolean(citySelect?.disabled);
        return {
          valid: false,
          field: citySelect,
          message: unavailable
            ? 'Delivery cities are still loading or temporarily unavailable. Please wait for the city list to load, then choose a delivery city.'
            : 'Please choose a delivery city before adding this item to cart.'
        };
      }

      if (!datePicker?.value) {
        return {
          valid: false,
          field: datePicker,
          message: 'Please choose a delivery date before adding this item to cart.'
        };
      }

      if (!timeslotSelect?.value) {
        const statusText = String(status?.textContent || '');
        const dateUnavailable = /(no delivery times|choose another delivery date|couldn.t confirm|unavailable)/i.test(statusText);
        return {
          valid: false,
          field: timeslotSelect?.disabled && dateUnavailable ? datePicker : timeslotSelect,
          message: dateUnavailable
            ? 'No valid delivery time slot is available for this date. Please choose another delivery date, then select an available time slot.'
            : timeslotSelect?.disabled
              ? 'Please wait for the available delivery time slots to load, then choose a delivery time slot.'
              : 'Please choose a delivery time slot before adding this item to cart.'
        };
      }

      if (specificWrap && !specificWrap.hidden && !specificSelect?.value) {
        return {
          valid: false,
          field: specificSelect,
          message: 'Please choose a specific delivery time for this time slot before adding this item to cart.'
        };
      }

      return { valid: true };
    };

    const enforceExplicitCityChoice = () => {
      if (!citySelect || citySelect.disabled) return;

      const values = Array.from(citySelect.options || []).map((option) => option.value);
      let explicitCity = sessionStorage.getItem(EXPLICIT_CITY_KEY) || '';

      if (explicitCity && !values.includes(explicitCity)) {
        sessionStorage.removeItem(EXPLICIT_CITY_KEY);
        explicitCity = '';
      }

      if (explicitCity && citySelect.value === explicitCity) return;

      if (citySelect.value) {
        suppressCityMarker = true;
        citySelect.value = '';
        sessionStorage.removeItem(LEGACY_CITY_KEY);
        citySelect.dispatchEvent(new Event('change', { bubbles: true }));
        suppressCityMarker = false;
      }
    };

    citySelect?.addEventListener('change', (event) => {
      if (suppressCityMarker) return;

      if (event.isTrusted) {
        if (citySelect.value) {
          sessionStorage.setItem(EXPLICIT_CITY_KEY, citySelect.value);
          sessionStorage.setItem(LEGACY_CITY_KEY, citySelect.value);
        } else {
          sessionStorage.removeItem(EXPLICIT_CITY_KEY);
          sessionStorage.removeItem(LEGACY_CITY_KEY);
        }
      }

      setRequiredState();
    });

    const cityObserver = citySelect
      ? new MutationObserver(() => {
          enforceExplicitCityChoice();
          setRequiredState();
        })
      : null;

    cityObserver?.observe(citySelect, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['disabled']
    });

    specificWrap && new MutationObserver(setRequiredState).observe(specificWrap, {
      attributes: true,
      attributeFilter: ['hidden']
    });

    form.addEventListener('submit', (event) => {
      setRequiredState();
      const result = deliveryValidation();
      if (result.valid) {
        closeModal({ restoreFocus: false });
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      openModal({
        title: 'Complete delivery details',
        message: result.message,
        field: result.field
      });
    }, true);

    modalAction?.addEventListener('click', focusMissingField);
    modalCloseControls.forEach((control) => {
      control.addEventListener('click', () => closeModal());
    });

    modal?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeModal();
        return;
      }

      if (event.key !== 'Tab' || !modalDialog) return;
      const focusable = Array.from(
        modalDialog.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')
      ).filter((element) => element instanceof HTMLElement && element.offsetParent !== null);

      if (!focusable.length) {
        event.preventDefault();
        modalDialog.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    [datePicker, timeslotSelect, specificSelect].forEach((field) => {
      field?.addEventListener('change', setRequiredState);
    });

    setRequiredState();
    enforceExplicitCityChoice();
  }

  function initAll(root = document) {
    if (root.matches?.('[data-fa-delivery-widget]')) initRules(root);
    root.querySelectorAll?.('[data-fa-delivery-widget]').forEach(initRules);
  }

  function observeRegion(region) {
    if (!region || observedRegions.has(region)) return;
    observedRegions.add(region);
    new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;
          initAll(node);
        }
      }
    }).observe(region, { childList: true, subtree: true });
  }

  function bind(root = document) {
    initAll(root);
    const widgets = [];
    if (root.matches?.('[data-fa-delivery-widget]')) widgets.push(root);
    root.querySelectorAll?.('[data-fa-delivery-widget]').forEach((widget) => widgets.push(widget));
    widgets.forEach((widget) => {
      const region = widget.closest('.shopify-section') || widget.closest('product-info') || widget;
      observeRegion(region);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => bind(document), { once: true });
  } else {
    bind(document);
  }

  document.addEventListener('shopify:section:load', (event) => bind(event.target));
})();
